import type { LandingPageContent } from './types'

// GitLab-Issue #145 (Schritt 5, ergänzt 14.08.2026): Schritt-für-Schritt-How-to-Seite für den
// B2B-Subunternehmer-Marktplatz, analog zu so-funktioniert-es-fuer-kunden.ts (#143) und
// so-funktioniert-es-fuer-handwerker.ts (#144) - dieselbe HowToStep-Infrastruktur, derselbe
// SeoLandingPage-Renderer. Jeder Schritt wurde live gegen die laufende QA-Docker-Umgebung
// nachvollzogen (zwei frische Handwerker-Test-Accounts "Elektro Nordlicht GmbH" und "Solar Süd
// Montage GmbH", echter B2B-Auftrag) - Screenshots liegen unter public/how-to/b2b/, mit
// Test-/Platzhalterdaten (Firmennamen, Preise und Passwörter sind bereits verworfene
// QA-Testdaten). Schritte 1-6 dokumentieren den Auftrags-Flow (DEMAND).
//
// Ergänzt 14.08.2026, nachdem Issue #145 Schritte 1-4 (Kapazitäten-Listing, SUPPLY) umgesetzt
// wurden (MRs !44-!47): Schritte 7-9 dokumentieren den zweiten Listing-Flow - freie Kapazität
// anbieten statt einen Auftrag zu suchen, mit umgekehrter Preisvorschlag- statt
// Gebot-Terminologie. Gleiches Vorgehen: live gegen einen lokalen Merge aus Backend-Step1 +
// Frontend-Step2/3 nachvollzogen (Test-Accounts "Elektro Süd Kapazität GmbH" und "Generalbau
// Nord GmbH"), echte Screenshots unter public/how-to/b2b/07-09.
export const SO_FUNKTIONIERT_ES_FUER_B2B_MARKTPLATZ: LandingPageContent = {
  slug: 'so-funktioniert-es-fuer-b2b-marktplatz',
  lang: 'de',
  topicId: 'so-funktioniert-es-fuer-b2b-marktplatz',
  title: 'So funktioniert der Roofora B2B-Marktplatz – Anleitung Schritt für Schritt',
  metaDescription:
    'Schritt-für-Schritt-Anleitung für den B2B-Subunternehmer-Marktplatz: Auftrag einstellen ' +
    'oder freie Kapazität anbieten, Angebote anderer Betriebe durchsuchen, Preisvorschlag/Gebot ' +
    'abgeben und Kontakt gezielt freigeben - mit echten Screenshots aus der Anwendung.',
  keywords: [
    'Roofora B2B Marktplatz Anleitung',
    'Subunternehmer Solar finden',
    'B2B Auftrag einstellen Photovoltaik',
    'Handwerksbetrieb sucht Subunternehmer Solar',
    'freie Kapazität Solar Handwerksbetrieb',
    'Kapazität anbieten Handwerksbetrieb',
  ],
  eyebrow: 'Anleitung für den B2B-Marktplatz',
  h1: 'So funktioniert der Roofora B2B-Marktplatz',
  intro:
    'Diese Anleitung zeigt jeden Schritt aus Sicht eines Handwerksbetriebs im B2B-Marktplatz - ' +
    'vom Auftrag einstellen über das Durchsuchen fremder Aufträge bis zur Kontaktfreigabe an ' +
    'einen Subunternehmer, sowie den umgekehrten Fall: freie Kapazität anbieten statt einen ' +
    'Auftrag zu suchen - mit echten Screenshots aus der Anwendung.',
  sections: [
    { type: 'heading', text: 'In 3 Minuten erklärt' },
    {
      type: 'paragraph',
      text:
        'Der B2B-Marktplatz läuft im selben Handwerker-Account wie die Endkundenanfragen, unter ' +
        '"B2B-Marktplatz" im Dashboard. Sie wählen beim Einstellen zwischen zwei Richtungen: ' +
        '"Auftrag" (Sie suchen einen Subunternehmer) oder "Kapazität" (Sie haben ein freies Team ' +
        'und suchen Arbeit) - beide laufen im selben durchsuchbaren Marktplatz. Bei den ' +
        'Angeboten anderer Betriebe reagieren Sie mit einem Gebot bzw. Preisvorschlag samt ' +
        'Kontaktanfrage. Genau wie bei Endkundenanfragen entscheidet der Ersteller des Listings ' +
        'individuell, wem er seine Kontaktdaten freigibt - kein Automatismus, keine pauschale ' +
        'Freigabe.',
    },
    { type: 'heading', text: 'Was brauche ich vorher?' },
    {
      type: 'list',
      items: [
        'Einen bestehenden Handwerker-Account (siehe Anleitung für Handwerksbetriebe)',
        'Für einen eigenen Auftrag oder eine Kapazität: Auftragstyp, PLZ und einen groben Zeitraum',
        'Für ein Gebot oder einen Preisvorschlag: einen ungefähren Preis oder zumindest eine Preisnotiz',
      ],
    },
  ],
  steps: [
    {
      title: 'Zugang zum B2B-Marktplatz',
      whatYouSee:
        'Im Handwerker-Dashboard eine eigene Kachel "B2B-Marktplatz" mit den Buttons ' +
        '"B2B-Aufträge durchsuchen" und "Meine B2B-Aufträge" - unabhängig von den Kacheln für ' +
        'Endkundenanfragen, Kontaktanfragen und Merkliste.',
      whatToDo: 'Klicken Sie auf "B2B-Aufträge durchsuchen", um den Marktplatz zu öffnen.',
      whatHappensNext: 'Die anonymisierte Liste aller offenen B2B-Aufträge anderer Betriebe öffnet sich.',
      successCriterion: 'Die Übersicht "B2B-Aufträge (anonymisiert)" ist sichtbar.',
      screenshot: {
        src: '/how-to/b2b/01-dashboard.jpg',
        alt: 'Handwerker-Dashboard mit eigener Kachel B2B-Marktplatz und den Buttons B2B-Aufträge durchsuchen und Meine B2B-Aufträge',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Marktplatz durchsuchen',
      whatYouSee:
        'Zwei Tabs "Endkundenanfragen" und "B2B-Aufträge" sowie darunter Filter nach Auftragstyp ' +
        'und PLZ. Eigene Aufträge werden hier nie angezeigt - der Marktplatz zeigt ausschließlich ' +
        'Aufträge anderer Betriebe.',
      whatToDo:
        'Filtern Sie optional nach Auftragstyp oder PLZ, um passende Aufträge in Ihrer Nähe zu finden, ' +
        'oder klicken Sie oben rechts auf "Auftrag einstellen", um selbst einen Subunternehmer zu suchen.',
      whatHappensNext: 'Passende Aufträge erscheinen als Liste mit PLZ, Zeitraum, Umfang und Anzahl Gebote.',
      successCriterion: 'Sie sehen entweder passende Aufträge oder den Hinweis "Aktuell keine offenen B2B-Aufträge".',
      screenshot: {
        src: '/how-to/b2b/02-marktplatz-uebersicht.jpg',
        alt: 'B2B-Marktplatz-Übersicht mit Tabs Endkundenanfragen/B2B-Aufträge, Filtern nach Auftragstyp und PLZ',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Eigenen Auftrag einstellen',
      whatYouSee:
        'Ein kompaktes Formular mit Auftragstyp (Elektroinstallation, Dachmontage, ' +
        'Komplettinstallation), Titel, Beschreibung, PLZ, Zeitraum von/bis, grobem Umfang und der ' +
        'Option "Kontakt gewünscht" - deutlich schlanker als der Kunden-Wizard, da ein B2B-Auftrag ' +
        'weniger Angaben braucht.',
      whatToDo:
        'Wählen Sie den Auftragstyp, beschreiben Sie kurz, was zu tun ist, und tragen Sie PLZ sowie ' +
        'einen groben Zeitraum ein. Klicken Sie danach entweder auf "Entwurf speichern" (weiter ' +
        'bearbeitbar, für andere Betriebe unsichtbar) oder direkt auf "Veröffentlichen".',
      whatHappensNext:
        'Nach "Veröffentlichen" ist der Auftrag anonymisiert für alle anderen Handwerksbetriebe im ' +
        'Marktplatz sichtbar - ohne Ihren Firmennamen.',
      successCriterion: 'Eine grüne Bestätigung "Auftrag veröffentlicht ✓" erscheint.',
      troubleshooting:
        'Ein Entwurf lässt sich beliebig oft bearbeiten, bevor Sie ihn veröffentlichen - nutzen Sie ' +
        '"Entwurf speichern", falls Ihnen noch Angaben fehlen.',
      screenshot: {
        src: '/how-to/b2b/03-auftrag-erstellen.jpg',
        alt: 'Ausgefülltes B2B-Auftragsformular mit Auftragstyp, Titel, Beschreibung, PLZ und Zeitraum',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Gebot abgeben und Kontakt anfragen',
      whatYouSee:
        'Bei einem fremden Auftrag in der Marktplatz-Liste ein Feld für einen Preis (optional), ein ' +
        'Feld für eine Preisnotiz und den Button "Gebot abgeben & Kontakt anfragen".',
      whatToDo:
        'Tragen Sie einen Preis und/oder eine kurze Preisnotiz ein und klicken Sie auf "Gebot abgeben ' +
        '& Kontakt anfragen" - beides wird in einem Schritt übermittelt, genau wie bei ' +
        'Endkundenanfragen.',
      whatHappensNext:
        'Ihr Gebot und Ihre Kontaktanfrage sind für Sie kostenlos und unverbindlich. Der ' +
        'auftraggebende Betrieb sieht beides und entscheidet individuell, ob er Sie freigibt.',
      successCriterion: 'Eine grüne Bestätigung "Gebot gesendet ✓" erscheint, darunter der Status "Kontaktanfrage gesendet – wird geprüft".',
      screenshot: {
        src: '/how-to/b2b/04-gebot-und-kontakt.jpg',
        alt: 'Bestätigung Gebot gesendet mit Preis und Status Kontaktanfrage gesendet – wird geprüft',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Gebote und Kontaktanfragen verwalten',
      whatYouSee:
        'Unter "Meine B2B-Aufträge" nach dem Aufklappen eines veröffentlichten Auftrags die Bereiche ' +
        '"Eingegangene Gebote" (mit Preis/Preisnotiz je Betrieb und dem Button "Gebot annehmen") und ' +
        '"Kontaktanfragen" (mit "Freigeben"/"Ablehnen" je Betrieb) - unabhängig voneinander, ein ' +
        'Gebot annehmen setzt keine automatische Kontaktfreigabe voraus.',
      whatToDo:
        'Prüfen Sie die eingegangenen Gebote und klicken Sie bei der Kontaktanfrage des gewünschten ' +
        'Betriebs auf "Freigeben".',
      whatHappensNext: 'Der freigegebene Betrieb sieht ab sofort Ihren Firmennamen in seiner Marktplatz-Ansicht.',
      successCriterion: 'Die Kontaktanfrage zeigt den Status "freigegeben" statt der Buttons "Freigeben"/"Ablehnen".',
      troubleshooting:
        'Ein "Gebot annehmen" schließt den Auftrag für weitere Gebote nicht automatisch ab - nutzen ' +
        'Sie danach bei Bedarf "Schließen", wenn der Auftrag vergeben ist.',
      screenshot: {
        src: '/how-to/b2b/05-kontaktanfrage-freigeben.jpg',
        alt: 'Aufgeklappter eigener B2B-Auftrag mit eingegangenem Gebot und einer Kontaktanfrage mit Freigeben-/Ablehnen-Buttons',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Kontakt erhalten',
      whatYouSee:
        'In der Marktplatz-Ansicht des anfragenden Betriebs bei diesem Auftrag die grüne Meldung ' +
        '"Kontaktanfrage angenommen" mit dem Firmennamen des auftraggebenden Betriebs.',
      whatToDo: 'Nehmen Sie über die angezeigten Kontaktdaten Kontakt zum auftraggebenden Betrieb auf.',
      whatHappensNext:
        'Die weitere Abstimmung (Termin, Details, Preis) läuft direkt zwischen den beiden Betrieben - ' +
        'die Plattform ist an dieser Stelle nicht mehr beteiligt, genau wie bei Endkundenanfragen.',
      successCriterion: 'Der Firmenname des auftraggebenden Betriebs (und, falls hinterlegt, E-Mail/Telefon) ist sichtbar.',
      troubleshooting:
        'Ohne hinterlegte E-Mail-Adresse im eigenen Profil werden Sie nicht aktiv benachrichtigt, wenn ' +
        'freigegeben wird - schauen Sie deshalb regelmäßig im B2B-Marktplatz vorbei.',
      screenshot: {
        src: '/how-to/b2b/06-kontakt-erhalten.jpg',
        alt: 'Marktplatz-Ansicht mit grüner Meldung Kontaktanfrage angenommen und Firmenname des auftraggebenden Betriebs',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Freie Kapazität statt eines Auftrags anbieten',
      whatYouSee:
        'Ganz oben im selben Formular ("B2B-Auftrag erstellen") die Auswahl "Auftrag (ich suche ' +
        'einen Subunternehmer)" oder "Kapazität (ich habe freie Kapazität)" - keine neuen Felder, ' +
        'nur andere Beschriftungen: "Zeitraum von/bis" heißt bei Kapazität "Verfügbar von/bis", ' +
        '"Grober Umfang" heißt "Kapazitätsumfang".',
      whatToDo:
        'Wählen Sie "Kapazität", tragen Sie Titel (z. B. "Elektro-Trupp frei ab KW 35, 2 Personen"), ' +
        'Beschreibung, PLZ und Verfügbarkeitsfenster ein und veröffentlichen Sie wie einen Auftrag.',
      whatHappensNext:
        'Die Kapazität erscheint anonymisiert im selben Marktplatz wie die Aufträge - mit dem Badge ' +
        '"Kapazität" statt "Auftrag", auch in Ihrer eigenen Übersicht "Meine B2B-Aufträge".',
      successCriterion: 'Eine grüne Bestätigung "Auftrag veröffentlicht ✓" erscheint, das Listing trägt danach das Badge "Kapazität".',
      screenshot: {
        src: '/how-to/b2b/07-kapazitaet-erstellen.jpg',
        alt: 'B2B-Formular mit Auswahl "Kapazität (ich habe freie Kapazität)" und ausgefülltem Titel',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Kapazitäten durchsuchen und Preisvorschlag machen',
      whatYouSee:
        'Im Marktplatz ein zweiter Filter neben dem Auftragstyp: "Aufträge & Kapazitäten" / "Nur ' +
        'Aufträge" / "Nur Kapazitäten". Bei einer Kapazität heißt es statt "Gebot" konsequent ' +
        '"Preisvorschlag" - "Mein Preisvorschlag", "Preisvorschläge: …" und der Button ' +
        '"Preisvorschlag senden & Kontakt anfragen".',
      whatToDo:
        'Filtern Sie auf "Nur Kapazitäten", tragen Sie bei einer passenden Kapazität einen ' +
        'Preisvorschlag und/oder eine Notiz ein und klicken Sie auf "Preisvorschlag senden & ' +
        'Kontakt anfragen".',
      whatHappensNext:
        'Anders als beim klassischen Auftrag treten Sie hier an den Kapazitätsanbieter heran, statt ' +
        'ihn zu unterbieten - fachlich: Sie hätten einen Auftrag für das freie Team.',
      successCriterion: 'Eine grüne Bestätigung "Preisvorschlag gesendet ✓" erscheint.',
      screenshot: {
        src: '/how-to/b2b/08-kapazitaet-durchsuchen.jpg',
        alt: 'B2B-Marktplatz mit Filter "Aufträge & Kapazitäten", Badge "Kapazität" und Feldern für Preisvorschlag',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Preisvorschlag prüfen und Kontakt freigeben',
      whatYouSee:
        'Unter "Meine B2B-Aufträge" bei der aufgeklappten Kapazität die Bereiche "Eingegangene ' +
        'Preisvorschläge" (mit Button "Preisvorschlag annehmen") und "Kontaktanfragen" (mit ' +
        '"Freigeben"/"Ablehnen") - exakt dieselbe Struktur wie beim Auftrag, nur umbenannt.',
      whatToDo:
        'Prüfen Sie die eingegangenen Preisvorschläge und klicken Sie bei der Kontaktanfrage des ' +
        'gewünschten Betriebs auf "Freigeben".',
      whatHappensNext:
        'Der freigegebene Betrieb sieht sofort Ihren Firmennamen in seiner Marktplatz-Ansicht - ' +
        'kein Payment-Schritt, identisch zum Auftrags-Fall.',
      successCriterion: 'Die Kontaktanfrage zeigt den Status "freigegeben" statt der Buttons "Freigeben"/"Ablehnen".',
      troubleshooting:
        'Ein maximaler Deckel an gleichzeitigen Kontaktfreigaben gilt für Kapazitäten identisch zum ' +
        'Auftrags-Fall - eine gefragte Kapazität in einer attraktiven Region kann trotzdem nicht ' +
        'unbegrenzt viele Kontaktanfragen gleichzeitig freigeben.',
      screenshot: {
        src: '/how-to/b2b/09-preisvorschlag-freigeben.jpg',
        alt: 'Aufgeklappte eigene Kapazität mit eingegangenem Preisvorschlag und einer Kontaktanfrage mit Freigeben-/Ablehnen-Buttons',
        width: 1120,
        height: 567,
      },
    },
  ],
  faq: [
    {
      q: 'Kostet die Nutzung des B2B-Marktplatzes etwas?',
      a:
        'Nein. Auftrag einstellen, Aufträge durchsuchen, Gebote abgeben und Kontakt anfragen sind ' +
        'aktuell kostenlos - wie im gesamten B2B-Marktplatz gibt es keinen Bezahlschritt.',
      },
    {
      q: 'Sehe ich, welcher Betrieb einen Auftrag eingestellt hat?',
      a:
        'Nein, nicht bevor dieser Betrieb Ihre Kontaktanfrage individuell freigibt - Aufträge im ' +
        'Marktplatz sind bis zur Freigabe anonymisiert.',
    },
    {
      q: 'Kann ich meinen eigenen Auftrag im Marktplatz sehen?',
      a:
        'Ihre eigenen Aufträge werden Ihnen in der Durchsuchen-Ansicht nicht angezeigt - dafür gibt es ' +
        '"Meine B2B-Aufträge" mit Geboten und Kontaktanfragen im Überblick.',
    },
    {
      q: 'Was passiert, wenn ich ein Gebot annehme?',
      a:
        'Das Gebot wird als angenommen markiert. Eine Kontaktfreigabe müssen Sie unabhängig davon ' +
        'zusätzlich erteilen - beides ist bewusst getrennt.',
    },
    {
      q: 'Gibt es auch die Möglichkeit, freie Kapazität statt eines Auftrags anzubieten?',
      a:
        'Ja. Wählen Sie beim Einstellen "Kapazität (ich habe freie Kapazität)" statt "Auftrag" - ' +
        'beide Richtungen laufen im selben Marktplatz mit denselben Mechaniken, siehe die Schritte ' +
        'weiter unten.',
    },
    {
      q: 'Warum heißt es bei Kapazitäten "Preisvorschlag" statt "Gebot"?',
      a:
        'Weil sich die Rollen umdrehen: Bei einem Auftrag unterbieten sich mehrere Betriebe um den ' +
        'Zuschlag. Bei einer Kapazität tritt stattdessen ein interessierter Betrieb mit einem ' +
        'Preisvorschlag an den Kapazitätsanbieter heran - kein Unterbieten, sondern ein Angebot.',
    },
  ],
  primaryCta: { label: 'Jetzt kostenlos als Handwerksbetrieb registrieren', action: 'craftsman' },
  secondaryCta: { label: 'Anleitung für Handwerksbetriebe ansehen', action: 'link', href: '/so-funktioniert-es-fuer-handwerker' },
  relatedLinks: [
    { label: 'So funktioniert Roofora für Handwerksbetriebe', href: '/so-funktioniert-es-fuer-handwerker' },
    { label: 'Für Handwerksbetriebe: mehr Aufträge finden', href: '/handwerker-photovoltaik-auftraege' },
  ],
}
